/**
 * Configuration Page
 * Manage working days configuration: location schedules and holidays
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Cog6ToothIcon, BuildingOffice2Icon, CalendarDaysIcon, WrenchScrewdriverIcon, UserGroupIcon, UsersIcon, ClipboardDocumentListIcon, CheckIcon, XMarkIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../auth/AuthContext';
import { canEditConfig, canEditSalesTeamsConfig } from '../auth/permissions';
import {
  getWorkingDaysConfig,
  saveLocationSchedule,
  saveHoliday,
  deleteHoliday,
  LocationScheduleConfig,
  HolidayConfig,
  SaturdayDepartmentSchedule,
  HolidayClosureType,
  HolidayDateType,
  TsmGroupAssignmentConfig,
  NutSheetTypesConfig,
  SalesTeamAssignmentConfig,
} from '../services/api';
import { WarrantySqueezeTab } from '../components/config/WarrantySqueezeTab';
import { TsmGroupsTab } from '../components/config/TsmGroupsTab';
import { NutSheetTypesTab } from '../components/config/NutSheetTypesTab';
import { SalesTeamsTab } from '../components/config/SalesTeamsTab';
import {
  CONFIGURABLE_SATURDAY_DEPARTMENTS,
  DEPARTMENT_DISPLAY_NAMES,
  DEFAULT_SATURDAY_SCHEDULE,
} from '../config/workingDaysConfig';

type TabType = 'locations' | 'holidays' | 'squeeze' | 'tsm-groups' | 'nut-sheet-types' | 'sales-teams';

const CLOSURE_TYPE_LABELS: Record<HolidayClosureType, string> = {
  ALL: 'All Departments Closed',
  PARTIAL_SALES_ONLY: 'Sales Open Only',
};

const DAY_OF_WEEK_LABELS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const MONTH_LABELS = ['', 'January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const WEEK_NUMBER_LABELS = ['', 'First', 'Second', 'Third', 'Fourth', 'Fifth'];

interface NewHolidayForm {
  name: string;
  displayName: string;
  closureType: HolidayClosureType;
  dateType: HolidayDateType;
  month: number;
  day: number;
  weekNumber: number;
  dayOfWeek: number;
}

const INITIAL_HOLIDAY_FORM: NewHolidayForm = {
  name: '',
  displayName: '',
  closureType: 'ALL',
  dateType: 'FIXED',
  month: 1,
  day: 1,
  weekNumber: 1,
  dayOfWeek: 1,
};

function getHolidayDateDescription(holiday: HolidayConfig): string {
  if (holiday.dateType === 'FIXED' && holiday.month && holiday.day) {
    return `${MONTH_LABELS[holiday.month]} ${holiday.day}`;
  } else if (holiday.dateType === 'NTH_WEEKDAY' && holiday.month && holiday.weekNumber !== undefined && holiday.dayOfWeek !== undefined) {
    return `${WEEK_NUMBER_LABELS[holiday.weekNumber]} ${DAY_OF_WEEK_LABELS[holiday.dayOfWeek]} of ${MONTH_LABELS[holiday.month]}`;
  } else if (holiday.dateType === 'LAST_WEEKDAY' && holiday.month && holiday.dayOfWeek !== undefined) {
    return `Last ${DAY_OF_WEEK_LABELS[holiday.dayOfWeek]} of ${MONTH_LABELS[holiday.month]}`;
  }
  return 'Unknown';
}

export const Configuration: React.FC = () => {
  const { userRole, assignedDepartmentIds } = useAuth();
  // Admin/CEO/Executive can edit everything; GM can view only.
  const canEdit = canEditConfig(userRole);
  // Sales Teams additionally editable by NEW-dept DepartmentManagers — for
  // them this page shows ONLY the Sales Teams tab (the route guard already
  // requires the NEW_VEHICLE assignment; the backend config-write gate in
  // lambda/shared/config-access.ts is the real enforcement boundary).
  const canEditSalesTeams = canEditSalesTeamsConfig(userRole, assignedDepartmentIds);
  const salesTeamsOnly = userRole === 'DepartmentManager';

  const [activeTab, setActiveTab] = useState<TabType>(salesTeamsOnly ? 'sales-teams' : 'locations');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [locationSchedules, setLocationSchedules] = useState<LocationScheduleConfig[]>([]);
  const [holidays, setHolidays] = useState<HolidayConfig[]>([]);
  const [tsmGroupAssignments, setTsmGroupAssignments] = useState<TsmGroupAssignmentConfig[]>([]);
  const [nutSheetTypes, setNutSheetTypes] = useState<NutSheetTypesConfig[]>([]);
  const [salesTeamAssignments, setSalesTeamAssignments] = useState<SalesTeamAssignmentConfig[]>([]);
  const [editingLocation, setEditingLocation] = useState<number | null>(null);
  const [editingHoliday, setEditingHoliday] = useState<string | null>(null);
  const [pendingLocationChange, setPendingLocationChange] = useState<{ locationId: number; saturdaySchedule: SaturdayDepartmentSchedule } | null>(null);
  const [pendingHolidayChange, setPendingHolidayChange] = useState<{ name: string; closureType: HolidayClosureType } | null>(null);
  const [saving, setSaving] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [showAddHolidayModal, setShowAddHolidayModal] = useState(false);
  const [newHoliday, setNewHoliday] = useState<NewHolidayForm>(INITIAL_HOLIDAY_FORM);
  const [holidayToDelete, setHolidayToDelete] = useState<HolidayConfig | null>(null);

  // silent=true refreshes the config data without flipping the page into its
  // loading spinner — the spinner unmounts the active tab and wipes its local
  // state (selected location/manager), which reads as a "page reload" to the
  // operator mid-workflow.
  const fetchConfig = useCallback(async (forceRefresh: boolean = false, silent: boolean = false) => {
    if (!silent) setLoading(true);
    setError(null);

    const response = await getWorkingDaysConfig(forceRefresh);

    if (response.success && response.data) {
      setLocationSchedules(response.data.locationSchedules);
      setHolidays(response.data.holidays);
      setTsmGroupAssignments(response.data.tsmGroupAssignments ?? []);
      setNutSheetTypes(response.data.nutSheetTypes ?? []);
      setSalesTeamAssignments(response.data.salesTeamAssignments ?? []);
    } else {
      setError(response.error || 'Failed to load configuration');
    }

    setLoading(false);
  }, []);

  useEffect(() => {
    // Always force refresh when loading config page to get latest data
    fetchConfig(true);
  }, [fetchConfig]);

  const handleLocationEdit = (location: LocationScheduleConfig) => {
    setEditingLocation(location.locationId);
    // Clone the current schedule to allow editing
    setPendingLocationChange({
      locationId: location.locationId,
      saturdaySchedule: { ...location.saturdaySchedule },
    });
  };

  const handleDepartmentToggle = (department: keyof SaturdayDepartmentSchedule) => {
    if (!pendingLocationChange) return;
    setPendingLocationChange({
      ...pendingLocationChange,
      saturdaySchedule: {
        ...pendingLocationChange.saturdaySchedule,
        [department]: !pendingLocationChange.saturdaySchedule[department],
      },
    });
  };

  const handleLocationSave = async () => {
    if (!pendingLocationChange) return;

    setSaving(true);
    setError(null);

    const location = locationSchedules.find(l => l.locationId === pendingLocationChange.locationId);
    if (!location) return;

    const response = await saveLocationSchedule({
      type: 'LOCATION_SCHEDULE',
      locationId: pendingLocationChange.locationId,
      locationName: location.locationName,
      saturdaySchedule: pendingLocationChange.saturdaySchedule,
    });

    if (response.success) {
      setSuccessMessage(`Updated ${location.locationName} Saturday schedule`);
      setTimeout(() => setSuccessMessage(null), 3000);
      // Re-fetch config with forceRefresh to invalidate Lambda cache
      await fetchConfig(true);
    } else {
      setError(response.error || 'Failed to save location schedule');
    }

    setEditingLocation(null);
    setPendingLocationChange(null);
    setSaving(false);
  };

  const handleLocationCancel = () => {
    setEditingLocation(null);
    setPendingLocationChange(null);
  };

  const handleHolidayClosureChange = (name: string, closureType: HolidayClosureType) => {
    setEditingHoliday(name);
    setPendingHolidayChange({ name, closureType });
  };

  const handleHolidaySave = async () => {
    if (!pendingHolidayChange) return;

    setSaving(true);
    setError(null);

    const holiday = holidays.find(h => h.name === pendingHolidayChange.name);
    if (!holiday) return;

    const response = await saveHoliday({
      type: 'HOLIDAY',
      name: holiday.name,
      displayName: holiday.displayName,
      closureType: pendingHolidayChange.closureType,
      dateType: holiday.dateType,
      month: holiday.month,
      day: holiday.day,
      weekNumber: holiday.weekNumber,
      dayOfWeek: holiday.dayOfWeek,
    });

    if (response.success) {
      setSuccessMessage(`Updated ${holiday.displayName} closure type`);
      setTimeout(() => setSuccessMessage(null), 3000);
      // Re-fetch config with forceRefresh to invalidate Lambda cache
      await fetchConfig(true);
    } else {
      setError(response.error || 'Failed to save holiday');
    }

    setEditingHoliday(null);
    setPendingHolidayChange(null);
    setSaving(false);
  };

  const handleHolidayCancel = () => {
    setEditingHoliday(null);
    setPendingHolidayChange(null);
  };

  const handleAddHoliday = async () => {
    if (!newHoliday.name.trim() || !newHoliday.displayName.trim()) {
      setError('Holiday name and display name are required');
      return;
    }

    setSaving(true);
    setError(null);

    const request: any = {
      type: 'HOLIDAY',
      name: newHoliday.name.trim(),
      displayName: newHoliday.displayName.trim(),
      closureType: newHoliday.closureType,
      dateType: newHoliday.dateType,
      month: newHoliday.month,
    };

    if (newHoliday.dateType === 'FIXED') {
      request.day = newHoliday.day;
    } else if (newHoliday.dateType === 'NTH_WEEKDAY') {
      request.weekNumber = newHoliday.weekNumber;
      request.dayOfWeek = newHoliday.dayOfWeek;
    } else if (newHoliday.dateType === 'LAST_WEEKDAY') {
      request.dayOfWeek = newHoliday.dayOfWeek;
    }

    const response = await saveHoliday(request);

    if (response.success) {
      setSuccessMessage(`Added holiday "${newHoliday.displayName}"`);
      setTimeout(() => setSuccessMessage(null), 3000);
      setShowAddHolidayModal(false);
      setNewHoliday(INITIAL_HOLIDAY_FORM);
      await fetchConfig(true);
    } else {
      setError(response.error || 'Failed to add holiday');
    }

    setSaving(false);
  };

  const handleDeleteHoliday = async () => {
    if (!holidayToDelete) return;

    setSaving(true);
    setError(null);

    const response = await deleteHoliday(holidayToDelete.name);

    if (response.success) {
      setSuccessMessage(`Deleted holiday "${holidayToDelete.displayName}"`);
      setTimeout(() => setSuccessMessage(null), 3000);
      setHolidayToDelete(null);
      await fetchConfig(true);
    } else {
      setError(response.error || 'Failed to delete holiday');
    }

    setSaving(false);
  };

  const tabButtonClass = (tab: TabType) => {
    const base = 'px-4 py-2 text-sm font-medium rounded-t-lg border-b-2 transition-colors';
    if (activeTab === tab) {
      return `${base} border-blue-600 text-blue-600 bg-white`;
    }
    return `${base} border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300`;
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="bg-white rounded-lg shadow-md">
        {/* Header */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center space-x-4">
            <div className="p-3 bg-gray-100 rounded-lg">
              <Cog6ToothIcon className="h-8 w-8 text-gray-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Configuration</h1>
              <p className="text-gray-500">
                {salesTeamsOnly
                  ? 'Manage sales team assignments for the Sales Team Report'
                  : 'Manage working days settings: location schedules and holidays'}
              </p>
            </div>
          </div>
        </div>

        {/* Success/Error Messages */}
        {successMessage && (
          <div className="mx-6 mt-4 p-3 bg-green-50 border border-green-200 rounded-md flex items-center space-x-2">
            <CheckIcon className="h-5 w-5 text-green-600" />
            <span className="text-green-800">{successMessage}</span>
          </div>
        )}

        {error && (
          <div className="mx-6 mt-4 p-3 bg-red-50 border border-red-200 rounded-md flex items-center space-x-2">
            <XMarkIcon className="h-5 w-5 text-red-600" />
            <span className="text-red-800">{error}</span>
          </div>
        )}

        {/* Role Notice — hidden on the Sales Teams tab when the viewer can edit
            it (GM, NEW-dept DeptMgr); GMs stay view-only on every other tab */}
        {!canEdit && !(activeTab === 'sales-teams' && canEditSalesTeams) && (
          <div className="mx-6 mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
            <span className="text-yellow-800">View only mode. Admin or Executive role required to make changes.</span>
          </div>
        )}

        {/* Tabs — DeptMgrs see ONLY Sales Teams; other config (schedules,
            holidays, squeeze, TSM groups, Nut Sheet types) stays GM+ */}
        <div className="px-6 pt-4 border-b border-gray-200">
          <div className="flex space-x-4">
            {!salesTeamsOnly && (
              <>
                <button
                  onClick={() => setActiveTab('locations')}
                  className={tabButtonClass('locations')}
                >
                  <div className="flex items-center space-x-2">
                    <BuildingOffice2Icon className="h-4 w-4" />
                    <span>Location Schedules</span>
                  </div>
                </button>
                <button
                  onClick={() => setActiveTab('holidays')}
                  className={tabButtonClass('holidays')}
                >
                  <div className="flex items-center space-x-2">
                    <CalendarDaysIcon className="h-4 w-4" />
                    <span>Holidays</span>
                  </div>
                </button>
                <button
                  onClick={() => setActiveTab('squeeze')}
                  className={tabButtonClass('squeeze')}
                >
                  <div className="flex items-center space-x-2">
                    <WrenchScrewdriverIcon className="h-4 w-4" />
                    <span>Warranty Squeeze</span>
                  </div>
                </button>
                <button
                  onClick={() => setActiveTab('tsm-groups')}
                  className={tabButtonClass('tsm-groups')}
                >
                  <div className="flex items-center space-x-2">
                    <UserGroupIcon className="h-4 w-4" />
                    <span>TSM Groups</span>
                  </div>
                </button>
                <button
                  onClick={() => setActiveTab('nut-sheet-types')}
                  className={tabButtonClass('nut-sheet-types')}
                >
                  <div className="flex items-center space-x-2">
                    <ClipboardDocumentListIcon className="h-4 w-4" />
                    <span>Nut Sheet Types</span>
                  </div>
                </button>
              </>
            )}
            <button
              onClick={() => setActiveTab('sales-teams')}
              className={tabButtonClass('sales-teams')}
            >
              <div className="flex items-center space-x-2">
                <UsersIcon className="h-4 w-4" />
                <span>Sales Teams</span>
              </div>
            </button>
          </div>
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <span className="ml-3 text-gray-600">Loading configuration...</span>
            </div>
          ) : activeTab === 'squeeze' ? (
            <WarrantySqueezeTab
              canEdit={canEdit}
              locationSchedules={locationSchedules}
              onSuccess={(message) => {
                setSuccessMessage(message);
                setTimeout(() => setSuccessMessage(null), 3000);
              }}
              onError={(message) => setError(message)}
            />
          ) : activeTab === 'tsm-groups' ? (
            <TsmGroupsTab
              canEdit={canEdit}
              locationSchedules={locationSchedules}
              tsmGroupAssignments={tsmGroupAssignments}
              onSuccess={(message) => {
                setSuccessMessage(message);
                setTimeout(() => setSuccessMessage(null), 3000);
              }}
              onError={(message) => setError(message)}
              refresh={() => fetchConfig(true, true)}
            />
          ) : activeTab === 'sales-teams' ? (
            <SalesTeamsTab
              canEdit={canEditSalesTeams}
              locationSchedules={locationSchedules}
              salesTeamAssignments={salesTeamAssignments}
              onSuccess={(message) => {
                setSuccessMessage(message);
                setTimeout(() => setSuccessMessage(null), 3000);
              }}
              onError={(message) => setError(message)}
              refresh={() => fetchConfig(true, true)}
            />
          ) : activeTab === 'nut-sheet-types' ? (
            <NutSheetTypesTab
              canEdit={canEdit}
              locationSchedules={locationSchedules}
              nutSheetTypes={nutSheetTypes}
              onSuccess={(message) => {
                setSuccessMessage(message);
                setTimeout(() => setSuccessMessage(null), 3000);
              }}
              onError={(message) => setError(message)}
              refresh={() => fetchConfig(true)}
            />
          ) : activeTab === 'locations' ? (
            <div>
              <p className="text-sm text-gray-600 mb-4">
                Configure which departments are open on Saturdays for each location.
                All locations are closed on Sundays and open Monday-Friday (except holidays).
                <span className="inline-flex items-center ml-2">
                  <span className="inline-block w-3 h-3 bg-green-500 rounded mr-1"></span>
                  <span className="text-xs text-gray-500">= Open</span>
                  <span className="inline-block w-3 h-3 bg-red-400 rounded ml-3 mr-1"></span>
                  <span className="text-xs text-gray-500">= Closed</span>
                </span>
              </p>

              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Location
                      </th>
                      {CONFIGURABLE_SATURDAY_DEPARTMENTS.map(dept => (
                        <th key={dept} className="px-2 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                          {DEPARTMENT_DISPLAY_NAMES[dept]}
                        </th>
                      ))}
                      {canEdit && (
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Actions
                        </th>
                      )}
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {locationSchedules.map(location => {
                      const isEditing = editingLocation === location.locationId;
                      const schedule = isEditing && pendingLocationChange
                        ? pendingLocationChange.saturdaySchedule
                        : location.saturdaySchedule;

                      return (
                        <tr key={location.locationId} className={isEditing ? 'bg-blue-50' : ''}>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">{location.locationName}</div>
                          </td>
                          {CONFIGURABLE_SATURDAY_DEPARTMENTS.map(dept => (
                            <td key={dept} className="px-2 py-3 text-center">
                              {isEditing && canEdit ? (
                                <button
                                  onClick={() => handleDepartmentToggle(dept)}
                                  disabled={saving}
                                  className={`w-6 h-6 rounded border-2 transition-colors ${
                                    schedule[dept]
                                      ? 'bg-green-500 border-green-600 hover:bg-green-600'
                                      : 'bg-red-400 border-red-500 hover:bg-red-500'
                                  } disabled:opacity-50`}
                                  title={schedule[dept] ? 'Open - click to close' : 'Closed - click to open'}
                                >
                                  {schedule[dept] ? (
                                    <CheckIcon className="w-4 h-4 text-white mx-auto" />
                                  ) : (
                                    <XMarkIcon className="w-4 h-4 text-white mx-auto" />
                                  )}
                                </button>
                              ) : (
                                <span
                                  className={`inline-block w-6 h-6 rounded ${
                                    schedule[dept] ? 'bg-green-500' : 'bg-red-400'
                                  }`}
                                  title={schedule[dept] ? 'Open on Saturday' : 'Closed on Saturday'}
                                >
                                  {schedule[dept] ? (
                                    <CheckIcon className="w-4 h-4 text-white mx-auto mt-1" />
                                  ) : (
                                    <XMarkIcon className="w-4 h-4 text-white mx-auto mt-1" />
                                  )}
                                </span>
                              )}
                            </td>
                          ))}
                          {canEdit && (
                            <td className="px-4 py-3 whitespace-nowrap text-right text-sm font-medium">
                              {isEditing ? (
                                <div className="flex justify-end space-x-2">
                                  <button
                                    onClick={handleLocationSave}
                                    disabled={saving}
                                    className="text-green-600 hover:text-green-900 disabled:opacity-50"
                                  >
                                    {saving ? 'Saving...' : 'Save'}
                                  </button>
                                  <button
                                    onClick={handleLocationCancel}
                                    disabled={saving}
                                    className="text-gray-600 hover:text-gray-900 disabled:opacity-50"
                                  >
                                    Cancel
                                  </button>
                                </div>
                              ) : (
                                <button
                                  onClick={() => handleLocationEdit(location)}
                                  className="text-blue-600 hover:text-blue-900"
                                >
                                  Edit
                                </button>
                              )}
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {locationSchedules.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  No location schedules configured. Run the seed script to initialize data.
                </div>
              )}
            </div>
          ) : (
            <div>
              <div className="flex justify-between items-start mb-4">
                <p className="text-sm text-gray-600">
                  Configure holiday closure rules. "All Closed" means all departments are closed.
                  "Sales Open Only" means only New and Used Vehicle departments are open.
                </p>
                {canEdit && (
                  <button
                    onClick={() => setShowAddHolidayModal(true)}
                    className="ml-4 inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    <PlusIcon className="h-4 w-4 mr-1" />
                    Add Holiday
                  </button>
                )}
              </div>

              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Holiday
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Date
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Closure Type
                      </th>
                      {canEdit && (
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Actions
                        </th>
                      )}
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {holidays.map(holiday => (
                      <tr key={holiday.name} className={editingHoliday === holiday.name ? 'bg-blue-50' : ''}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">{holiday.displayName}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-500">{getHolidayDateDescription(holiday)}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {editingHoliday === holiday.name && canEdit ? (
                            <select
                              value={pendingHolidayChange?.closureType || holiday.closureType}
                              onChange={(e) => handleHolidayClosureChange(holiday.name, e.target.value as HolidayClosureType)}
                              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                            >
                              {Object.entries(CLOSURE_TYPE_LABELS).map(([value, label]) => (
                                <option key={value} value={value}>{label}</option>
                              ))}
                            </select>
                          ) : (
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                              holiday.closureType === 'ALL' ? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800'
                            }`}>
                              {CLOSURE_TYPE_LABELS[holiday.closureType]}
                            </span>
                          )}
                        </td>
                        {canEdit && (
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            {editingHoliday === holiday.name ? (
                              <div className="flex justify-end space-x-2">
                                <button
                                  onClick={handleHolidaySave}
                                  disabled={saving}
                                  className="text-green-600 hover:text-green-900 disabled:opacity-50"
                                >
                                  {saving ? 'Saving...' : 'Save'}
                                </button>
                                <button
                                  onClick={handleHolidayCancel}
                                  disabled={saving}
                                  className="text-gray-600 hover:text-gray-900 disabled:opacity-50"
                                >
                                  Cancel
                                </button>
                              </div>
                            ) : (
                              <div className="flex justify-end space-x-3">
                                <button
                                  onClick={() => {
                                    setEditingHoliday(holiday.name);
                                    setPendingHolidayChange({ name: holiday.name, closureType: holiday.closureType });
                                  }}
                                  className="text-blue-600 hover:text-blue-900"
                                >
                                  Edit
                                </button>
                                <button
                                  onClick={() => setHolidayToDelete(holiday)}
                                  className="text-red-600 hover:text-red-900"
                                >
                                  <TrashIcon className="h-4 w-4" />
                                </button>
                              </div>
                            )}
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {holidays.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  No holidays configured. Run the seed script to initialize data.
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Add Holiday Modal */}
      {showAddHolidayModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-md shadow-lg rounded-md bg-white">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">Add New Holiday</h3>
              <button
                onClick={() => {
                  setShowAddHolidayModal(false);
                  setNewHoliday(INITIAL_HOLIDAY_FORM);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Holiday Name (ID)</label>
                <input
                  type="text"
                  value={newHoliday.name}
                  onChange={(e) => setNewHoliday({ ...newHoliday, name: e.target.value })}
                  placeholder="e.g., VETERANS_DAY"
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                />
                <p className="mt-1 text-xs text-gray-500">Used internally (letters, numbers, underscores)</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Display Name</label>
                <input
                  type="text"
                  value={newHoliday.displayName}
                  onChange={(e) => setNewHoliday({ ...newHoliday, displayName: e.target.value })}
                  placeholder="e.g., Veterans Day"
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Closure Type</label>
                <select
                  value={newHoliday.closureType}
                  onChange={(e) => setNewHoliday({ ...newHoliday, closureType: e.target.value as HolidayClosureType })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                >
                  {Object.entries(CLOSURE_TYPE_LABELS).map(([value, label]) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Date Type</label>
                <select
                  value={newHoliday.dateType}
                  onChange={(e) => setNewHoliday({ ...newHoliday, dateType: e.target.value as HolidayDateType })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                >
                  <option value="FIXED">Fixed Date (e.g., July 4th)</option>
                  <option value="NTH_WEEKDAY">Nth Weekday (e.g., First Monday)</option>
                  <option value="LAST_WEEKDAY">Last Weekday (e.g., Last Monday)</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Month</label>
                <select
                  value={newHoliday.month}
                  onChange={(e) => setNewHoliday({ ...newHoliday, month: parseInt(e.target.value) })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                >
                  {MONTH_LABELS.slice(1).map((label, index) => (
                    <option key={index + 1} value={index + 1}>{label}</option>
                  ))}
                </select>
              </div>

              {newHoliday.dateType === 'FIXED' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700">Day</label>
                  <input
                    type="number"
                    min="1"
                    max="31"
                    value={newHoliday.day}
                    onChange={(e) => setNewHoliday({ ...newHoliday, day: parseInt(e.target.value) || 1 })}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                  />
                </div>
              )}

              {newHoliday.dateType === 'NTH_WEEKDAY' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700">Week Number</label>
                  <select
                    value={newHoliday.weekNumber}
                    onChange={(e) => setNewHoliday({ ...newHoliday, weekNumber: parseInt(e.target.value) })}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                  >
                    {WEEK_NUMBER_LABELS.slice(1).map((label, index) => (
                      <option key={index + 1} value={index + 1}>{label}</option>
                    ))}
                  </select>
                </div>
              )}

              {(newHoliday.dateType === 'NTH_WEEKDAY' || newHoliday.dateType === 'LAST_WEEKDAY') && (
                <div>
                  <label className="block text-sm font-medium text-gray-700">Day of Week</label>
                  <select
                    value={newHoliday.dayOfWeek}
                    onChange={(e) => setNewHoliday({ ...newHoliday, dayOfWeek: parseInt(e.target.value) })}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                  >
                    {DAY_OF_WEEK_LABELS.map((label, index) => (
                      <option key={index} value={index}>{label}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            <div className="mt-6 flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowAddHolidayModal(false);
                  setNewHoliday(INITIAL_HOLIDAY_FORM);
                }}
                disabled={saving}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleAddHoliday}
                disabled={saving || !newHoliday.name.trim() || !newHoliday.displayName.trim()}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                {saving ? 'Adding...' : 'Add Holiday'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {holidayToDelete && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-md shadow-lg rounded-md bg-white">
            <div className="flex items-center mb-4">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                <TrashIcon className="h-6 w-6 text-red-600" />
              </div>
              <h3 className="ml-3 text-lg font-medium text-gray-900">Delete Holiday</h3>
            </div>

            <p className="text-sm text-gray-500 mb-4">
              Are you sure you want to delete <strong>{holidayToDelete.displayName}</strong>?
              This action cannot be undone. Working days calculations will no longer account for this holiday.
            </p>

            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setHolidayToDelete(null)}
                disabled={saving}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteHoliday}
                disabled={saving}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 disabled:opacity-50"
              >
                {saving ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
